import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  EconomicOperatorSize,
  usePreviewContractAwardNoticeQuery,
  useSubmitContractAwardNoticeMutation,
  type ContractAwardNoticePreviewDto,
  type ContractAwardNoticeWinnerDto,
  type FieldErrorDto,
} from "../../../autogen/bff-api";
import {
  DoffinRefusalAlert,
  doffinRefusal,
  isDpsSchemeNoticeMissing,
  isResultNoticeAlreadySent,
} from "./DoffinRefusalAlert";
import { DoffinNoAnswerAlert } from "./DoffinNoAnswerAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "./doffinNoAnswer";
import { ConcludedDateField } from "./ConcludedDateField";
import type { StandstillEnd } from "./concludedDate";
import { concludedDateProblem } from "./concludedDate";
import { useApiError } from "../../../common/errors/useApiError";
import {
  formatDecimalForInput,
  parseDecimalInput,
  type ParsedDecimal,
} from "../../../common/input/parseDecimalInput";
import { ListLoadError } from "../../../common/ListLoadError";
import { ModalSkeleton } from "../../../common/ModalSkeleton";

interface Props {
  eventId: string;
  concludedOn: string | null | undefined;
  awardDecisionDay: string | null;
  standstill: StandstillEnd | null;
  contractFrom?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface WinnerEdit {
  contractValue?: string;
  size?: EconomicOperatorSize;
}

interface MissingValue {
  businessTerm: string;
  supplierId: string;
  ruleId?: string;
  text: string;
}

const CONTRACT_VALUE = "BT-720";

// eForms subtypes 36 and 37: the result of a design contest, which awards no contract.
const resultsDesignContest = (form: string | null | undefined): boolean => form === "36" || form === "37";
const WINNER_SIZE = "BT-165";
const BUYER_EDITABLE = [CONTRACT_VALUE, WINNER_SIZE];
const FROM_SUPPLIER_PROFILE = ["BT-501", "BT-512", "BT-513"];

export const AnnounceResultModal = ({
  eventId,
  concludedOn,
  awardDecisionDay,
  standstill,
  contractFrom = null,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger, triggerSoft, message } = useApiError();
  const [date, setDate] = useState(concludedOn ?? "");
  const [edits, setEdits] = useState<Record<string, WinnerEdit>>({});
  const [refusal, setRefusal] = useState<FieldErrorDto[] | null>(null);
  const [schemeNoticeMissing, setSchemeNoticeMissing] = useState(false);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const {
    data: preview,
    error: previewError,
    isFetching: isFetchingPreview,
    refetch: refetchPreview,
  } = usePreviewContractAwardNoticeQuery(
    { sourcingEventId: eventId },
    { refetchOnMountOrArgChange: true },
  );
  const [submit, { isLoading: isSubmitting }] =
    useSubmitContractAwardNoticeMutation();

  const contractValueText = (winner: ContractAwardNoticeWinnerDto) =>
    edits[winner.bidId]?.contractValue ??
    (winner.contractValue?.value != null
      ? formatDecimalForInput(winner.contractValue.value)
      : "");
  const contractValueOf = (winner: ContractAwardNoticeWinnerDto) =>
    parseDecimalInput(contractValueText(winner), { allowSpaceGrouping: true });
  const sizeOf = (winner: ContractAwardNoticeWinnerDto) =>
    winner.size ?? edits[winner.bidId]?.size;
  const edit = (bidId: string, change: WinnerEdit) =>
    setEdits((current) => ({
      ...current,
      [bidId]: { ...current[bidId], ...change },
    }));

  const designContest = resultsDesignContest(preview?.form);
  const missing = preview ? stillMissing(preview, contractValueOf, sizeOf, designContest) : [];
  const anyInvalidContractValue =
    !designContest &&
    (preview?.winners ?? []).some((winner) => contractValueProblem(contractValueOf(winner)) != null);

  const send = async () => {
    if (preview == null) return;
    setRefusal(null);
    setNoAnswer(null);
    try {
      await submit({
        sourcingEventId: eventId,
        submitContractAwardNoticeRequest: {
          contractConcludedOn: designContest ? null : date,
          winners: preview.winners.map((winner) => {
            const contractValue = contractValueOf(winner);
            return {
              bidId: winner.bidId,
              contractValue:
                !designContest && contractValue.kind === "number" ? contractValue.value : null,
              size: sizeOf(winner) ?? null,
            };
          }),
        },
      }).unwrap();
      toast({
        title: t("The result notice is sent to Doffin"),
        status: "success",
        position: "top-right",
      });
      onClose();
    } catch (e) {
      const breaches = doffinRefusal(e);
      const unanswered = doffinNoAnswer(e);
      if (breaches) setRefusal(breaches);
      else if (unanswered) setNoAnswer(unanswered);
      else if (isDpsSchemeNoticeMissing(e)) setSchemeNoticeMissing(true);
      else if (isResultNoticeAlreadySent(e)) {
        triggerSoft(e);
        onClose();
      } else trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("Announce the result on Doffin")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {schemeNoticeMissing || isDpsSchemeNoticeMissing(previewError) ? (
            <DoffinRefusalAlert
              breaches={[]}
              heading={t(
                "We could not find the notice of the dynamic purchasing system, so the result cannot be linked to it. Nothing has been sent.",
              )}
            />
          ) : preview == null && previewError != null ? (
            <ListLoadError
              message={message(previewError)}
              onRetry={refetchPreview}
              isRetrying={isFetchingPreview}
            />
          ) : preview == null ? (
            <ModalSkeleton />
          ) : (
            <Stack spacing="5">
              <Box>
                <Text fontWeight="semibold">{t("Outcome")}</Text>
                <Text>{designContest ? t("Winners chosen") : t("Contract awarded")}</Text>
              </Box>
              <Box>
                <Text fontWeight="semibold" mb="2">
                  {designContest ? t("Winners") : t("Winners and contract value")}
                </Text>
                <Stack spacing="4">
                  {preview.winners.map((winner) => (
                    <WinnerValues
                      key={winner.bidId}
                      winner={winner}
                      contractValueText={contractValueText(winner)}
                      contractValue={contractValueOf(winner)}
                      size={sizeOf(winner)}
                      asksContractValue={!designContest}
                      onChange={(change) => edit(winner.bidId, change)}
                    />
                  ))}
                </Stack>
              </Box>
              {!designContest && (
                <ConcludedDateField
                  id="announce-concluded-on"
                  value={date}
                  onChange={setDate}
                  earliest={awardDecisionDay}
                  standstill={standstill}
                  contractFrom={contractFrom}
                />
              )}
              <Text color="smMuted" fontSize="sm">
                {t("Sent on eForms {{form}}", { form: preview.form })}
              </Text>
              {missing.length > 0 && (
                <MissingValues preview={preview} missing={missing} />
              )}
              {refusal && (
                <DoffinRefusalAlert breaches={refusal} />
              )}
              {noAnswer && <DoffinNoAnswerAlert noAnswer={noAnswer} />}
              <Flex justify="end">
                <Button
                  colorScheme="teal"
                  isDisabled={
                    (!designContest &&
                      (date === "" || concludedDateProblem(date, awardDecisionDay, undefined, contractFrom) != null)) ||
                    missing.length > 0 ||
                    anyInvalidContractValue ||
                    noAnswer === "whileSending"
                  }
                  isLoading={isSubmitting}
                  onClick={send}
                >
                  {t("Announce on Doffin")}
                </Button>
              </Flex>
            </Stack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type ContractValueProblem = "notANumber" | "notPositive" | "tooLarge";

const contractValueProblem = (
  parsed: ParsedDecimal,
): ContractValueProblem | null => {
  if (parsed.kind === "notANumber") return "notANumber";
  if (parsed.kind !== "number") return null;
  if (parsed.value <= 0) return "notPositive";
  if (parsed.value > Number.MAX_SAFE_INTEGER) return "tooLarge";
  return null;
};

const stillMissing = (
  preview: ContractAwardNoticePreviewDto,
  contractValueOf: (winner: ContractAwardNoticeWinnerDto) => ParsedDecimal,
  sizeOf: (
    winner: ContractAwardNoticeWinnerDto,
  ) => EconomicOperatorSize | undefined | null,
  designContest: boolean,
): MissingValue[] => {
  const missingFor = (
    businessTerm: string,
    supplierId: string,
  ): MissingValue => ({
    businessTerm,
    supplierId,
    ruleId: preview.missing.find(
      (m) => m.businessTerm === businessTerm && m.supplierId === supplierId,
    )?.ruleId,
    text: "",
  });
  const fromBuyer = preview.winners.flatMap((winner) => [
    ...(designContest || contractValueOf(winner).kind === "number"
      ? []
      : [missingFor(CONTRACT_VALUE, winner.supplierId)]),
    ...(sizeOf(winner) != null
      ? []
      : [missingFor(WINNER_SIZE, winner.supplierId)]),
  ]);
  const otherwise = preview.missing.filter(
    (m) => m.businessTerm !== CONTRACT_VALUE && m.businessTerm !== WINNER_SIZE,
  );
  return [...fromBuyer, ...otherwise];
};

const WinnerValues = ({
  winner,
  contractValueText,
  contractValue,
  size,
  asksContractValue,
  onChange,
}: {
  winner: ContractAwardNoticeWinnerDto;
  contractValueText: string;
  contractValue: ParsedDecimal;
  size: EconomicOperatorSize | undefined | null;
  asksContractValue: boolean;
  onChange: (change: WinnerEdit) => void;
}) => {
  const { t } = useTranslation();
  const valueId = `contract-value-${winner.bidId}`;
  const problem = contractValueProblem(contractValue);
  const sizeId = `winner-size-${winner.bidId}`;
  return (
    <Box border="1px solid" borderColor="smBorder" rounded="md" p="3">
      <Text fontWeight="medium" mb="2">
        {winner.supplierName}
      </Text>
      <Stack direction={{ base: "column", md: "row" }} spacing="3">
        <FormControl isInvalid={problem != null} display={asksContractValue ? undefined : "none"}>
          <FormLabel htmlFor={valueId} fontSize="sm">
            {t("Contract value ({{currency}})", {
              currency: winner.contractValue?.currency ?? Currency.Nok,
            })}
          </FormLabel>
          <Input
            id={valueId}
            inputMode="decimal"
            autoComplete="off"
            backgroundColor="smBackground"
            value={contractValueText}
            onChange={(e) => onChange({ contractValue: e.target.value })}
          />
          <FormErrorMessage>
            {problem === "notPositive"
              ? t("The contract value must be greater than 0.")
              : problem === "tooLarge"
                ? t("The contract value is too large to be sent exactly.")
                : t("Write the amount as a number, for example 1 234,50")}
          </FormErrorMessage>
        </FormControl>
        {winner.size == null && (
          <FormControl>
            <FormLabel htmlFor={sizeId} fontSize="sm">
              {t("Company size")}
            </FormLabel>
            <Select
              id={sizeId}
              backgroundColor="smBackground"
              placeholder={t("Choose the size") ?? undefined}
              value={size ?? ""}
              onChange={(e) =>
                onChange({
                  size: (e.target.value || undefined) as
                    | EconomicOperatorSize
                    | undefined,
                })
              }
            >
              <option value={EconomicOperatorSize.Micro}>
                {t("Micro enterprise")}
              </option>
              <option value={EconomicOperatorSize.Small}>
                {t("Small enterprise")}
              </option>
              <option value={EconomicOperatorSize.Medium}>
                {t("Medium-sized enterprise")}
              </option>
              <option value={EconomicOperatorSize.Large}>
                {t("Large enterprise")}
              </option>
            </Select>
          </FormControl>
        )}
      </Stack>
    </Box>
  );
};

const MissingValues = ({
  preview,
  missing,
}: {
  preview: ContractAwardNoticePreviewDto;
  missing: MissingValue[];
}) => {
  const { t } = useTranslation();
  const fromSupplierProfile = missing.some((value) =>
    FROM_SUPPLIER_PROFILE.includes(value.businessTerm),
  );
  const onlySupportCanFix = missing.some(
    (value) =>
      !BUYER_EDITABLE.includes(value.businessTerm) &&
      !FROM_SUPPLIER_PROFILE.includes(value.businessTerm),
  );
  const supplierName = (value: MissingValue) =>
    preview.winners.find((winner) => winner.supplierId === value.supplierId)
      ?.supplierName ?? "";
  const describe = (value: MissingValue) => {
    switch (value.businessTerm) {
      case CONTRACT_VALUE:
        return t("The contract value is missing for {{supplier}}", {
          supplier: supplierName(value),
        });
      case WINNER_SIZE:
        return t("Choose the company size for {{supplier}} in the field above.", {
          supplier: supplierName(value),
        });
      default:
        return value.text;
    }
  };
  return (
    <Alert status="warning" alignItems="start">
      <AlertIcon />
      <AlertDescription>
        <Text>
          {t("Doffin requires these values before the notice can be sent:")}
        </Text>
        <List>
          {missing.map((value) => (
            <ListItem key={`${value.supplierId}-${value.businessTerm}`}>
              {describe(value)}
              {value.ruleId ? ` (${value.ruleId})` : ""}
            </ListItem>
          ))}
        </List>
        {(preview.registerUnavailableSupplierIds ?? []).map((supplierId) => (
          <Text mt="2" key={`register-${supplierId}`}>
            {t(
              "The Brønnøysund Register Centre isn't answering right now, so we couldn't fetch the details for {{supplier}}. Please try again later.",
              {
                supplier:
                  preview.winners.find((winner) => winner.supplierId === supplierId)?.supplierName ?? "",
              },
            )}
          </Text>
        ))}
        {fromSupplierProfile && (
          <Text mt="2">
            {t(
              "The organisation number, town and postcode come from the supplier's profile. Ask the supplier to update it, and the notice can be sent.",
            )}
          </Text>
        )}
        {onlySupportCanFix && (
          <Text mt="2">
            {t("Nothing in this dialog can correct this. Contact support.")}
          </Text>
        )}
      </AlertDescription>
    </Alert>
  );
};
